import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// 最大标签数量限制
export const MAX_TABS = 10

export interface Tab {
  id: string
  path: string
  title: string
  icon?: string
  lastAccessedAt: number
  canClose: boolean
}

interface TabsState {
  tabs: Tab[]
  activeTabId: string | null
  addTab: (tab: Omit<Tab, 'id' | 'lastAccessedAt' | 'canClose'> & { canClose?: boolean }) => string
  removeTab: (id: string) => void
  setActiveTab: (id: string) => void
  updateTab: (id: string, updates: Partial<Omit<Tab, 'id'>>) => void
  closeOtherTabs: (id: string) => void
  closeAllTabs: () => void
  reorderTabs: (fromIndex: number, toIndex: number) => void
  getOverflowTabs: (visibleCount: number) => Tab[]
  getLRUTab: () => Tab | null
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9)
}

/**
 * 找到最近最少使用的标签（LRU）
 * 排除当前活动标签和不可关闭的标签
 */
function findLRUTab(tabs: Tab[], activeTabId: string | null): Tab | null {
  const closableTabs = tabs.filter((t) => t.canClose && t.id !== activeTabId)
  if (closableTabs.length === 0) {
    // 如果没有可关闭的非活动标签，尝试找任何可关闭的标签
    const anyClosable = tabs.filter((t) => t.canClose)
    if (anyClosable.length === 0) {
      return null
    }
    return anyClosable.reduce((oldest, current) =>
      current.lastAccessedAt < oldest.lastAccessedAt ? current : oldest
    )
  }
  return closableTabs.reduce((oldest, current) =>
    current.lastAccessedAt < oldest.lastAccessedAt ? current : oldest
  )
}

export const useTabsStore = create<TabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,

      addTab: (tab) => {
        const { tabs } = get()
        const now = Date.now()

        // 检查是否已存在相同路径的标签
        const existing = tabs.find((t) => t.path === tab.path)
        if (existing) {
          // 更新访问时间
          set({
            tabs: tabs.map((t) =>
              t.id === existing.id ? { ...t, lastAccessedAt: now } : t
            ),
            activeTabId: existing.id,
          })
          return existing.id
        }

        const id = generateId()
        const newTab: Tab = {
          ...tab,
          id,
          lastAccessedAt: now,
          canClose: tab.canClose ?? true,
        }

        let newTabs = [...tabs, newTab]

        // 如果超过最大标签数量，使用 LRU 算法淘汰
        while (newTabs.length > MAX_TABS) {
          const lruTab = findLRUTab(newTabs, id) // 新标签是活动的，排除它
          if (lruTab) {
            newTabs = newTabs.filter((t) => t.id !== lruTab.id)
          } else {
            // 无法淘汰任何标签（所有标签都不可关闭），强制移除最旧的
            const oldest = newTabs.reduce((o, c) =>
              c.lastAccessedAt < o.lastAccessedAt ? c : o
            )
            newTabs = newTabs.filter((t) => t.id !== oldest.id)
          }
        }

        set({ tabs: newTabs, activeTabId: id })
        return id
      },

      removeTab: (id) => {
        const { tabs, activeTabId } = get()
        const tabToRemove = tabs.find((t) => t.id === id)

        // 不可关闭的标签不能被移除
        if (!tabToRemove || !tabToRemove.canClose) {
          return
        }

        const index = tabs.findIndex((t) => t.id === id)
        const newTabs = tabs.filter((t) => t.id !== id)
        let newActiveId = activeTabId

        // 如果关闭的是当前激活的标签，切换到相邻标签
        if (activeTabId === id && newTabs.length > 0) {
          const newIndex = Math.min(index, newTabs.length - 1)
          newActiveId = newTabs[newIndex].id
        } else if (newTabs.length === 0) {
          newActiveId = null
        }

        set({ tabs: newTabs, activeTabId: newActiveId })
      },

      setActiveTab: (id) => {
        const { tabs } = get()
        const now = Date.now()

        // 更新访问时间
        set({
          tabs: tabs.map((t) =>
            t.id === id ? { ...t, lastAccessedAt: now } : t
          ),
          activeTabId: id,
        })
      },

      updateTab: (id, updates) => {
        set((state) => ({
          tabs: state.tabs.map((t) => (t.id === id ? { ...t, ...updates } : t)),
        }))
      },

      closeOtherTabs: (id) => {
        const { tabs } = get()
        const tab = tabs.find((t) => t.id === id)
        if (tab) {
          // 保留目标标签和所有不可关闭的标签
          const remainingTabs = tabs.filter((t) => t.id === id || !t.canClose)
          set({ tabs: remainingTabs, activeTabId: id })
        }
      },

      closeAllTabs: () => {
        const { tabs } = get()
        // 保留不可关闭的标签
        const remainingTabs = tabs.filter((t) => !t.canClose)
        const newActiveId = remainingTabs.length > 0 ? remainingTabs[0].id : null
        set({ tabs: remainingTabs, activeTabId: newActiveId })
      },

      reorderTabs: (fromIndex, toIndex) => {
        set((state) => {
          const newTabs = [...state.tabs]
          const [removed] = newTabs.splice(fromIndex, 1)
          newTabs.splice(toIndex, 0, removed)
          return { tabs: newTabs }
        })
      },

      getOverflowTabs: (visibleCount) => {
        const { tabs } = get()
        if (tabs.length <= visibleCount) {
          return []
        }
        return tabs.slice(visibleCount)
      },

      getLRUTab: () => {
        const { tabs, activeTabId } = get()
        return findLRUTab(tabs, activeTabId)
      },
    }),
    {
      name: 'tabs-storage',
      partialize: (state) => ({
        tabs: state.tabs.slice(0, MAX_TABS),
        activeTabId: state.activeTabId,
      }),
    }
  )
)
